<script setup lang="ts">
import moment from "moment-timezone";
import { capitalize, computed, ref, watch } from "vue";

import Button from "~/components/dumb/Button.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import NumberInput from "~/components/dumb/NumberInput.vue";
import TimePicker from "~/components/dumb/TimePicker.vue";
import { DAY_OF_WEEK_LETTERS_AND_NUMBERS, RECURRENCE_KIND_TO_VALUE_MAP } from "~/constants/time";
import { ChevronDownIcon, RecurrenceIcon, XIcon } from "~/icons";
import { DropdownMenuItemKind, EditorMode, IconSize, RecurrenceKind } from "~/shared/enums";
import type { DayOfWeek, Recurrence } from "~/shared/types";
import { useUserStore } from "~/stores";
import { at8am } from "~/utils/date";
import { getRecurrenceSummary, getRelativeTimeForDatesDate, normalizeRecursNextAt } from "~/utils/time";

import DatePicker from "./DatePicker.vue";

const props = defineProps<{
  recurrence: Recurrence | null;
  recursNextAt: string | null;
  editorMode?: EditorMode;
  showTime?: boolean;
  expanded?: boolean;
  small?: boolean;
  isContrast?: boolean;
  editableStartingDate?: boolean;
}>();

const emit = defineEmits<{
  changeRecurrence: [recurrence: Recurrence | null];
  changeRecursNextAt: [recursNextAt: string | null];
}>();

const recurrenceDropdownRef = ref<InstanceType<typeof DropdownMenu> | null>(null);
const userStore = useUserStore();

const dowConfigNorm = computed(() => {
  const offset = userStore.firstDayOfWeek ?? 1;
  return [...DAY_OF_WEEK_LETTERS_AND_NUMBERS.slice(offset), ...DAY_OF_WEEK_LETTERS_AND_NUMBERS.slice(0, offset)];
});

const hasRecurrence = computed(() => !!props.recurrence && !!props.recursNextAt);

const interval = ref(props.recurrence?.interval ?? 1);
const kind = ref(props.recurrence?.kind ?? RecurrenceKind.DAILY);
const weeklyDays = ref(
  props.recurrence?.kind === RecurrenceKind.WEEKLY
    ? props.recurrence.days
    : [((moment().isoWeekday() + 6) % 7) as DayOfWeek]
);

const datetimeForTime = ref(moment().toISOString());
const setDatetimeForTime = () => {
  const start = moment(props.recursNextAt ?? undefined).startOf("minute");
  const remainder = (15 - (start.minute() % 15)) % 15;
  datetimeForTime.value = start.add(remainder, "minutes").toISOString();
};
setDatetimeForTime();

const showStartingDate = computed(() => {
  if (kind.value === RecurrenceKind.MONTHLY || kind.value === RecurrenceKind.YEARLY) {
    return true;
  }

  return (kind.value === RecurrenceKind.DAILY || kind.value === RecurrenceKind.WEEKLY) && interval.value > 1;
});

watch(
  () => props.recurrence,
  (newRecurrence) => {
    if (!newRecurrence) {
      return;
    }

    interval.value = newRecurrence.interval;
    kind.value = newRecurrence.kind;
    weeklyDays.value =
      newRecurrence.kind === RecurrenceKind.WEEKLY
        ? newRecurrence.days
        : [((moment().isoWeekday() + 6) % 7) as DayOfWeek];
  }
);

watch(() => props.recursNextAt, setDatetimeForTime);

const plural = computed(() => (interval.value > 1 ? "s" : ""));

const recurrenceSummary = computed(() => {
  if (!props.recurrence || !props.recursNextAt) {
    return null;
  }

  return getRecurrenceSummary(props.recurrence, moment(props.recursNextAt));
});

const deleteRecurrence = () => {
  emit("changeRecurrence", null);
  emit("changeRecursNextAt", null);
};

const emitRecurrenceChange = (recurrence: Recurrence) => {
  const baseDate = props.recursNextAt ? moment(props.recursNextAt) : moment();
  const newRecursNextAt = at8am(normalizeRecursNextAt(baseDate, recurrence)).toISOString();

  emit("changeRecurrence", recurrence);
  emit("changeRecursNextAt", newRecursNextAt);
};

const updateRecurrence = () => {
  const recurrence = {
    kind: kind.value,
    interval: interval.value,
  } as Recurrence;
  if (recurrence.kind === RecurrenceKind.WEEKLY) {
    recurrence.days = weeklyDays.value;
  }

  emitRecurrenceChange(recurrence);
};

const updateRecurrenceNumber = (newNumber: number) => {
  interval.value = newNumber;
  updateRecurrence();
};

const toggleSelectedDay = (day: DayOfWeek) => {
  if (weeklyDays.value.includes(day)) {
    if (weeklyDays.value.length === 1) {
      return;
    }
    weeklyDays.value = weeklyDays.value.filter((d) => d !== day);
  } else {
    weeklyDays.value = [...weeklyDays.value, day].sort();
  }
  updateRecurrence();
};

const SIMPLE_RECURRENCES: Recurrence[] = [
  {
    kind: RecurrenceKind.DAILY,
    interval: 1,
  },
  {
    kind: RecurrenceKind.WEEKLY,
    interval: 1,
    days: [((moment().isoWeekday() + 6) % 7) as DayOfWeek],
  },
  {
    kind: RecurrenceKind.WEEKLY,
    interval: 1,
    days: [0, 1, 2, 3, 4],
  },
  {
    kind: RecurrenceKind.MONTHLY,
    interval: 1,
  },
  {
    kind: RecurrenceKind.YEARLY,
    interval: 1,
  },
];

const simpleRecurrenceOptions = [
  ...SIMPLE_RECURRENCES.map((recurrence) => {
    const title = getRecurrenceSummary(recurrence, moment());
    return {
      title: capitalize(title),
      recurrence,
    };
  }),
  { title: "Custom", recurrence: SIMPLE_RECURRENCES[0] },
];

const recurrenceDropdownSections = computed(() => [
  {
    title: "Interval",
    items: simpleRecurrenceOptions.map(({ title, recurrence }) => ({
      title,
      kind: DropdownMenuItemKind.BUTTON,
      onClick: () => emitRecurrenceChange(recurrence),
    })),
  },
]);

const intervalDropdownSections = computed(() => [
  {
    title: "Recurrence",
    items: [...RECURRENCE_KIND_TO_VALUE_MAP.entries()].map(([currKind, title]) => ({
      title,
      kind: DropdownMenuItemKind.BUTTON,
      disabled: currKind === kind.value,
      onClick: () => {
        kind.value = currKind;
        updateRecurrence();
      },
    })),
  },
]);
</script>

<template>
  <!-- When no recurrence is set, render inline options instead of dropdown -->
  <div v-if="expanded && !hasRecurrence">
    <div class="mb-2 ml-0.5 flex items-center gap-2 text-sm text-md">
      <RecurrenceIcon class="icon-sm" />
      <div class="font-medium select-none">Set repetition</div>
    </div>
    <div class="flex flex-col gap-1">
      <button
        v-for="opt in simpleRecurrenceOptions"
        :key="opt.title"
        type="button"
        class="flex w-full items-center justify-between gap-2 rounded-sm border border-md py-[6px] pr-2 pl-3 text-sm text-md focus-ring-std hover:bg-lt"
        :class="isContrast ? 'hover:bg-md' : ''"
        @click="emitRecurrenceChange(opt.recurrence)">
        <span>{{ opt.title }}</span>
      </button>
    </div>
  </div>

  <DropdownMenu
    v-else-if="!expanded"
    ref="recurrenceDropdownRef"
    :disabled="hasRecurrence"
    :sections="recurrenceDropdownSections">
    <button
      type="button"
      class="flex w-full items-center justify-between gap-2 rounded-sm border border-md py-[5px] pr-2 pl-3 text-sm text-lt focus-ring-std"
      :class="[hasRecurrence && 'cursor-default', !hasRecurrence && 'hover:border-hvy']">
      <div class="flex items-center gap-2">
        <RecurrenceIcon class="icon-sm" :class="hasRecurrence && 'text-recurrence-base'" />
        <div class="text-left select-none" :class="hasRecurrence ? 'text-md' : 'text-vlt'">
          {{ hasRecurrence ? `Repeats ${recurrenceSummary}` : "Set repetition" }}
        </div>
      </div>
      <div
        v-if="hasRecurrence"
        class="cursor-pointer rounded-sm p-0.5 hover:bg-md"
        @click.stop="deleteRecurrence"
        @keydown.enter.stop="deleteRecurrence">
        <XIcon class="icon-xs text-lt" />
      </div>
    </button>
  </DropdownMenu>

  <div v-if="hasRecurrence" class="flex flex-col pl-1 text-md" :class="small ? 'gap-2' : 'gap-5'">
    <div class="flex items-center gap-4">
      <div class="text-sm">Every</div>
      <div class="flex items-center gap-2">
        <NumberInput :value="interval" label="interval" :min="1" @change="updateRecurrenceNumber" />

        <DropdownMenu :sections="intervalDropdownSections" :width-pixels="120" skip-nesting-level>
          <Button
            class="h-8 w-[85px] !justify-between border border-md py-2 pr-1 pl-3 text-xs"
            text-style="text-lt"
            :class="isContrast ? 'hover:bg-md' : 'hover:bg-lt'"
            :text="`${RECURRENCE_KIND_TO_VALUE_MAP.get(kind)}${plural}`"
            :icon-after="ChevronDownIcon"
            :icon-after-args="{ class: 'text-lt' }"
            :icon-size="IconSize.S"
            is-contrast />
        </DropdownMenu>
      </div>
    </div>

    <div v-if="kind === RecurrenceKind.WEEKLY" class="flex flex-col gap-2">
      <div class="flex items-center gap-4">
        <div class="text-sm">On</div>
        <div class="flex items-center" :class="small ? 'gap-1' : 'gap-2'">
          <button
            v-for="[dowLetter, dowNumber] in dowConfigNorm"
            :key="dowNumber"
            type="button"
            class="flex shrink-0 items-center justify-center rounded-full text-center text-xs transition-colors"
            :class="[
              small ? 'size-7' : 'size-8',
              weeklyDays.includes(dowNumber)
                ? 'hover-bg-primary bg-primary-base text-oncolor'
                : `border border-md text-lt ${isContrast ? 'hover:bg-md' : 'hover:bg-lt'}`,
            ]"
            @click="toggleSelectedDay(dowNumber)">
            {{ dowLetter }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showStartingDate" class="flex items-center gap-4">
      <span class="text-sm">Starting</span>
      <DatePicker
        :value="recursNextAt"
        :distance="-1"
        :editor-mode="editorMode ?? EditorMode.FORM"
        :recurrence="recurrence"
        :recurs-next-at="recursNextAt"
        :disabled="!editableStartingDate"
        @select="(selectedDate) => emit('changeRecursNextAt', selectedDate)">
        <div
          class="flex w-full items-center rounded-sm border border-md px-2 py-[7px] text-xs text-lt"
          :class="editableStartingDate && 'cursor-pointer'">
          {{ recursNextAt ? getRelativeTimeForDatesDate(recursNextAt) : "" }}
        </div>
      </DatePicker>
    </div>

    <TimePicker
      v-if="showTime"
      custom
      :value="datetimeForTime"
      :interval-min="15"
      @select="(newValue: string) => emit('changeRecursNextAt', newValue)" />
  </div>
</template>

<style scoped>
:deep(.vc-time-date) {
  display: none;
}
:deep(.vc-time-picker) {
  padding: 0px !important;
}
</style>
